import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionMenu, Box, Column, DataTable, IconButton, SimpleLabel, Svg } from '@hopskipdrive/react-components';
import PeopleIcon from '@mui/icons-material/People';
import * as Constants from '@rideiq/constants';
import { InvoiceGroup } from '@rideiq/organizers/features/InvoiceGroups/types';
import { useInvoiceGroups } from '@rideiq/organizers/hooks/useInvoiceGroups';
import { optionsToString } from '@rideiq/reports/util/queryToFilter';
import { TablePaginationContainer } from '@rideiq/shared/components/TablePaginationContainer';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import { useNavigation } from '@rideiq/shared/hooks/useNavigation';

export const InvoiceGroupsTable = ({ shouldRefetch, onRefetched }) => {
  const { invoiceGroups, fetchInvoiceGroups, isFetching, totalCount } = useInvoiceGroups();
  const { navigateTo } = useNavigation();
  const navigate = useNavigate();

  const columns: Column[] = [
    {
      label: 'Invoice Group Name',
      align: 'left',
      valueGetter: (details) => (
        <Box display="flex" alignItems="center" gap="6px">
          {details.name}
          {details.isDefault && (
            <SimpleLabel
              label="Default"
              bgColor="neutral95"
              style={{ color: variables.colorPalette.neutralVariant60 }}
            />
          )}
        </Box>
      ),
    },
    {
      key: 'poNumber',
      label: 'Purchase Group Number',
      align: 'left',
    },
    {
      key: 'passengerCount',
      label: 'Riders',
      align: 'left',
    },
    {
      label: 'Actions',
      align: 'center',
      valueGetter: (group: InvoiceGroup) => (
        <ActionMenu
          menuItems={[
            <ActionMenu.MenuItem
              key="edit-invoice-group"
              component="button"
              icon={<ActionMenu.MenuItemIcon icon="edit-filled" />}
              onClick={() => navigate(`/settings/invoice_groups/${group.id}/edit`, { state: { invoiceGroup: group } })}
              sx={{ gap: '20px' }}
            >
              Edit invoice group
            </ActionMenu.MenuItem>,
            <ActionMenu.MenuItem
              key="edit-riders"
              component="button"
              icon={<PeopleIcon />}
              onClick={() => {
                const searchParams = new URLSearchParams({
                  invoiceGroups: optionsToString([{ value: String(group.id), label: group.name }]) || '',
                  state: 'active',
                  query: '',
                });

                navigateTo(`/riders?${searchParams.toString()}`);
              }}
              sx={{ gap: '20px' }}
            >
              Edit riders
            </ActionMenu.MenuItem>,
            <ActionMenu.MenuItem
              key="delete"
              component="button"
              disabled={group.isDefault}
              icon={<ActionMenu.MenuItemIcon icon="trashcan" style={{ color: colorTokens.error.dark, opacity: 1 }} />}
              onClick={() =>
                navigate(`/settings/invoice_groups/${group.id}/delete`, { state: { invoiceGroup: group } })
              }
              sx={{ gap: '20px', color: colorTokens.error.dark }}
            >
              Delete
            </ActionMenu.MenuItem>,
          ]}
          slotProps={{
            root: {
              hideBackdrop: true,
            },
          }}
        >
          <IconButton aria-label="Open action menu for this invoice group" data-testid="more-button">
            <Svg height="20px" icon="more-horizontal" style={{ marginRight: 0 }} />
          </IconButton>
        </ActionMenu>
      ),
    },
  ];

  useEffect(() => {
    fetchInvoiceGroups({ page: 1, perPage: Constants.DEFAULT_ROWS_PER_PAGE });
  }, [fetchInvoiceGroups]);

  useEffect(() => {
    if (shouldRefetch) {
      fetchInvoiceGroups({ page: 1, perPage: Constants.DEFAULT_ROWS_PER_PAGE });
      onRefetched();
    }
  }, [shouldRefetch]);

  return (
    <TablePaginationContainer handlePageChange={fetchInvoiceGroups} totalCount={totalCount} isFetching={isFetching}>
      <DataTable
        stickyHeader={true}
        rows={invoiceGroups}
        isLoading={isFetching}
        columns={columns}
        allowRowClick={() => false}
        handleRowClick={() => null}
      />
    </TablePaginationContainer>
  );
};
